'use client';
import React, { useCallback, useState } from 'react';

import FloatyInput from '@nerdwallet/react-floaty-input';
import { Box } from '@nerdwallet/currency/components/Box';
import { Link } from '@nerdwallet/currency/components/Link';
import { SecureEncryptionMessage } from '@nerdwallet/currency/components/SecureEncryptionMessage';

import { useLazyQuery, useMutation } from '@apollo/client';
import { SegmentPageView } from '@lib/segment';
import ContinueButton from '../components/continue-button';
import styles from './otp-passcode-entry.module.scss';
import FriendlyTitle from '../components/friendly-title';
import { useAnalytics } from '@lib/segment';
import { useTuVisibilityContext } from '@components/transunion-flow/tu-visibility-context';
import {
  TuStep,
  useTuStepper,
} from '@components/transunion-flow/tu-modal-stepper';
import {
  AUTHENTICATE_CREDIT_PROFILE_QUESTIONS_MUTATION,
  TU_AUTHENTICATION_STATUS_PROFILE_QUERY,
} from '../queries';
import {
  hasOtpPasscodeQuestions,
  isTUBlocked,
} from '@components/transunion-flow/utils';
import { TuCreditReportIdentityProfile } from '@generated-gql/generated-types';

const isValid = (code) => {
  return code?.length && code?.length >= 4 && code?.length <= 10;
};

const codeErrorMsg = 'That’s the wrong code, check it and try again.';

interface Props {
  onActivationSuccess: (profile: TuCreditReportIdentityProfile) => void;
}

export const OtpPasscodeEntryStep = ({ onActivationSuccess }: Props) => {
  const { segmentTrack } = useAnalytics();
  const { setTuVisibility } = useTuVisibilityContext();
  const { authStatusProfile, setAuthStatusProfile } = useTuStepper();
  const [otpPasscodeForm, setOtpPasscodeForm] = useState({
    code: '',
    validation: {
      code: {
        isValid: false,
        feedback: null,
      },
    },
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [getAuthenticationStatusProfile] = useLazyQuery(
    TU_AUTHENTICATION_STATUS_PROFILE_QUERY,
  );

  const [setTUAuthentication] = useMutation(
    AUTHENTICATE_CREDIT_PROFILE_QUESTIONS_MUTATION,
  );

  const onSubmitCode = useCallback(
    async (resend) => {
      setIsLoading(true);

      try {
        const response = await setTUAuthentication({
          variables: {
            answers: [
              {
                questionId: authStatusProfile.questions[0].id,
                answerId: authStatusProfile.questions[0].answers[0].id,
                userInputAnswer: otpPasscodeForm.code,
              },
            ],
            resetPasscode: resend ? true : undefined,
          },
        });

        if (response.data?.authenticateCreditProfileQuestions) {
          // The backend will return either a TuCreditReportIdentityProfile
          // if the authentication succeeded or CreditProfileAuthStatusProfile
          const responseData = response.data.authenticateCreditProfileQuestions;
          if (responseData.__typename === 'TuCreditReportIdentityProfile') {
            onActivationSuccess(responseData);
            return;
          } else {
            setAuthStatusProfile(
              response.data.authenticateCreditProfileQuestions,
            );
            // If a new passcode was resent or a user inputted the wrong passcode,
            // TU will return more questions
            if (
              hasOtpPasscodeQuestions(
                response.data.authenticateCreditProfileQuestions,
              )
            ) {
              // If a user requested a new code, clear the field and remove errors
              setOtpPasscodeForm({
                code: '',
                validation: {
                  code: {
                    isValid: false,
                    feedback: [resend ? null : codeErrorMsg],
                  },
                },
              });
            }
          }
        }
      } catch (e) {
        // Refetch the latest state of the Auth Status Profile to see if the user is blocked
        const { data } = await getAuthenticationStatusProfile({
          fetchPolicy: 'network-only',
        });
        // If a user retried too many times, they will get blocked
        if (data?.tuAuthenticationStatusProfile) {
          setAuthStatusProfile(data.tuAuthenticationStatusProfile);
          if (isTUBlocked(data.tuAuthenticationStatusProfile)) {
            setTuVisibility({
              show: true,
              tuStep: TuStep.TU_BLOCKED,
            });
          }
        }
      } finally {
        setIsLoading(false);
      }
    },
    [
      setTUAuthentication,
      authStatusProfile.questions,
      otpPasscodeForm.code,
      setTuVisibility,
      setAuthStatusProfile,
      getAuthenticationStatusProfile,
      onActivationSuccess,
    ],
  );

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      if (isLoading) return;
      onSubmitCode(false);
      segmentTrack('Verification Code Submitted', {
        flow_name: 'TU Activation',
      });
    },
    [isLoading, onSubmitCode, segmentTrack],
  );

  const handleResendPasscode = useCallback(
    (e) => {
      e.preventDefault();
      if (isLoading) return;
      onSubmitCode(true);
      segmentTrack('Send Another Code Clicked', {
        flow_name: 'TU Activation',
      });
    },
    [isLoading, onSubmitCode, segmentTrack],
  );

  const handleChange = useCallback((e) => {
    const finalValue = e.target.value.replace(/\D/g, '');

    const finalValueValid = isValid(finalValue);

    setOtpPasscodeForm((prevState) => ({
      ...prevState,
      code: finalValue,
      validation: {
        code: {
          isValid: finalValueValid,
          feedback: finalValueValid ? null : prevState.validation.code.feedback,
        },
      },
    }));
  }, []);

  return (
    <SegmentPageView name="sms_code_verification">
      <Box display="flex" flexDirection="column">
        <FriendlyTitle title="Check your phone and enter your code below" />
        <form onSubmit={handleSubmit}>
          <div className={styles.inputContainer}>
            <FloatyInput
              label="Security code"
              value={otpPasscodeForm.code}
              onChange={handleChange}
              onBlur={handleChange}
              maxLength={10}
              minLength={4}
              error={
                !otpPasscodeForm.validation.code.isValid &&
                !!otpPasscodeForm.validation.code.feedback
              }
              errorMessage={otpPasscodeForm.validation.code.feedback}
            />
          </div>

          <Box marginTop={4} marginBottom={2}>
            Didn’t get it?{' '}
            <Link
              onClick={handleResendPasscode}
              disabled={isLoading}
              aria-label="resend code"
            >
              Send another code
            </Link>
          </Box>

          <Box marginTop={4}>
            <ContinueButton
              isDisabled={!otpPasscodeForm.validation.code.isValid || isLoading}
              isLoading={isLoading}
              buttonText="Submit code"
            />
          </Box>
          <Box marginTop={4}>
            <SecureEncryptionMessage />
          </Box>
        </form>
      </Box>
    </SegmentPageView>
  );
};

export default OtpPasscodeEntryStep;
